import { clear, createStore, delMany, get, setMany } from 'idb-keyval';
import {
    type CallbackNode,
    unstable_cancelCallback as cancelCallback,
    unstable_LowPriority as LowPriority,
    unstable_scheduleCallback as scheduleCallback,
} from 'scheduler';

import { Logger, reportException, SentryTags, Trace } from '@gitbook/util-debug';
import { filterOutNullable } from '@gitbook/util-ts';

import type { SuspenceBrowserCache } from './browserCache';
import { type SuspenseCacheEffect, SuspenseCacheEntryState } from './cache';

const DEBOUNCE_TIMEOUT = 1000;
const CACHE_KEEPALIVE_PERIOD = 7 * 24 * 60 * 60 * 1000;

type CacheEntry<Result> = {
    cacheKey: string;
    value: Result;
    lastFlushedAt: number;
};

/**
 * Effect to store the cache in the browser IndexedDB.
 */
export function createBrowserCacheEffect<Result>(dbName: string): SuspenceBrowserCache<Result> {
    const debugName = `browsercache:${dbName}`;
    const logger = Logger(debugName);

    let store: ReturnType<typeof createStore> | null = null;

    try {
        store = createStore(dbName, 'entries');
    } catch (error) {
        reportIndexedDBError(error);
        logger.error('error while creating IndexedDB store:', error);
    }

    const pendingKeys = new Set<string>();

    let pendingTimeout: ReturnType<typeof setTimeout> | null = null;
    let scheduledNode: CallbackNode | null = null;

    const clearCache = async () => {
        try {
            logger.info('clear cache');
            pendingKeys.clear();
            if (!store) {
                return;
            }
            await clear(store);
        } catch (error) {
            reportIndexedDBError(error);
            logger.error('error while clearing cache:', error);
        }
    };

    const effect: SuspenseCacheEffect<Result> = (cache) => {
        /**
         * Write all keys that changed to the database
         */
        async function flushToDatabase() {
            logger.info(`flush ${pendingKeys.size} to the browser database`);
            if (!store) {
                if (pendingKeys.size > 0) {
                    logger.info(
                        'IndexedDB store not available, dropping pending cache keys from memory'
                    );
                    pendingKeys.clear();
                }
                return;
            }

            try {
                const flushTrace = new Trace(`${debugName}.flush`, { sampleRate: 0 });
                await flushTrace.wrap(async () => {
                    const now = Date.now();

                    const keysToFlush = Array.from(pendingKeys.values());
                    pendingKeys.clear();

                    const entriesToSet = keysToFlush
                        .map((cacheKey) => {
                            const result = cache.getKey(cacheKey, true);
                            if (!result) {
                                return null;
                            }

                            // Ignore unset value in cache (ex: when currently fetching, etc)
                            const { state, value } = result;
                            if (state !== SuspenseCacheEntryState.LIVE || value === undefined) {
                                return null;
                            }

                            return [cacheKey, { cacheKey, value, lastFlushedAt: now }] as [
                                string,
                                CacheEntry<Result>,
                            ];
                        })
                        .filter(filterOutNullable);

                    if (entriesToSet.length > 0) {
                        await setMany(entriesToSet, store);
                    }
                });

                const cleanupTrace = new Trace(`${debugName}.cleanup`, { sampleRate: 0 });
                await cleanupTrace.wrap(async () => {
                    const cutoff = Date.now() - CACHE_KEEPALIVE_PERIOD;
                    await new Promise<void>((resolve, reject) => {
                        const request = indexedDB.open(dbName);
                        request.onupgradeneeded = () => {
                            const db = request.result;
                            if (!db.objectStoreNames.contains('entries')) {
                                db.createObjectStore('entries');
                            }
                        };
                        request.onsuccess = () => {
                            const db = request.result;
                            const transaction = db.transaction('entries', 'readwrite');
                            const store = transaction.objectStore('entries');
                            const cursorRequest = store.openCursor();

                            cursorRequest.onsuccess = () => {
                                const cursor = cursorRequest.result;
                                if (!cursor) {
                                    return;
                                }

                                const value = cursor.value as
                                    | CacheEntry<unknown>
                                    | undefined
                                    | null;
                                const lastFlushedAt =
                                    value && typeof value.lastFlushedAt === 'number'
                                        ? value.lastFlushedAt
                                        : null;
                                if (lastFlushedAt !== null && lastFlushedAt < cutoff) {
                                    cursor.delete();
                                }
                                cursor.continue();
                            };

                            cursorRequest.onerror = () => {
                                transaction.abort();
                            };

                            const finalize = (error?: Error | null) => {
                                db.close();
                                if (error) {
                                    reject(error);
                                    return;
                                }
                                resolve();
                            };

                            transaction.oncomplete = () => finalize();
                            transaction.onabort = () => finalize(transaction.error);
                            transaction.onerror = () => finalize(transaction.error);
                        };
                        request.onerror = () =>
                            reject(
                                request.error ?? new Error('error while opening IndexedDB database')
                            );
                    });
                });
            } catch (error) {
                reportIndexedDBError(error);
                logger.error('error while flushing to database:', error);
            }
        }

        /**
         * Debounce the flush to the database when a key has changed
         */
        function flushKeyToDatabase(key: string) {
            pendingKeys.add(key);

            if (pendingTimeout !== null) {
                clearTimeout(pendingTimeout);
            }

            if (scheduledNode !== null) {
                cancelCallback(scheduledNode);
            }

            pendingTimeout = setTimeout(() => {
                pendingTimeout = null;

                scheduledNode = scheduleCallback(LowPriority, () => {
                    void flushToDatabase().finally(() => {
                        scheduledNode = null;
                    });
                });
            }, DEBOUNCE_TIMEOUT);
        }

        //
        // Hook to initialize a key from the cache when it is first read.
        //
        cache.onInitializeKey(async (key) => {
            if (!store) {
                return undefined;
            }
            try {
                const result = await get<CacheEntry<Result>>(key, store);
                if (!result) {
                    return undefined;
                }

                logger.info(`initializing ${key} from browser cache`);
                return {
                    result: result.value,
                    setAt: result.lastFlushedAt,
                };
            } catch (error) {
                reportIndexedDBError(error);
                logger.error('error while reading from cache:', error);
                try {
                    await delMany([key], store);
                } catch {
                    // ignore
                }
                return undefined;
            }
        });

        //
        // When keys are changing, we flush to the database.
        //
        cache.onSet((key) => {
            flushKeyToDatabase(key);
        });
    };

    return { effect, clearCache };
}

function reportIndexedDBError(error: unknown): void {
    reportException(error instanceof Error ? error : new Error(String(error)), {
        tags: {
            [SentryTags.ERROR_CATEGORY]: 'browser-cache-indexedDB',
        },
    });
}
